import styled from '@emotion/styled';

import {
    getHeadlineFontFamily,
    getHeadlineFontSize,
    getHeadlineFontWeight,
    getHeadlineLineHeight,
    getPFontSize,
    getPFontWeight,
    getPLetterSpacing,
    getPLineHeight,
    getPTextTransform
} from './utils';

import type {HeadlineSize, PSize} from './utils';

/**
 * Props for the {@link Headline} styled component.
 * Controls the visual size variant and text alignment of headings.
 */
interface HeadlineProps {
    /** Optional text alignment. Defaults to `'left'`. */
    $align?: 'center' | 'left' | 'right';
    /**
     * The typography size variant.
     * - `'display'` — 4.4rem NotoSerif Bold (hero titles, splash screens)
     * - `'headline'` — 2.4rem NotoSerif Bold (card titles, screen headlines)
     * - `'title'` — 1.6rem PlusJakartaSans Medium (list entries, tabs).
     *
     * @default 'headline'
     */
    $size?: HeadlineSize;
}

/**
 * A styled heading component that renders as `<h2>` by default.
 * Use the `as` prop to change the semantic HTML element (h1–h6).
 * Use the `$size` prop to select among display, headline, or title typography.
 *
 * @param props        The styled component props including transient style props.
 * @param props.theme  The Emotion theme providing color and font tokens.
 * @param props.$align The optional text alignment override.
 * @returns A styled heading element with the specified typography and alignment.
 * @example
 * ```tsx
 * <Headline as="h1" $size="display">Hero Title</Headline>
 * <Headline $size="headline">Section Title</Headline>
 * <Headline as="h3" $size="title">Subsection</Headline>
 * ```
 */
export const Headline = styled.h2<HeadlineProps>`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-family: ${getHeadlineFontFamily};
    font-size: ${getHeadlineFontSize};
    font-weight: ${getHeadlineFontWeight};
    hyphens: auto;
    line-height: ${getHeadlineLineHeight};
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-wrap: balance;
`;

/**
 * Props for the {@link P} styled component.
 * Controls the visual size variant and text alignment of paragraphs and labels.
 */
interface ParagraphProps {
    /** Optional text alignment. Defaults to `'left'`. */
    $align?: 'center' | 'left' | 'right';
    /**
     * The typography size variant.
     * - `'body'` — 1.5rem PlusJakartaSans Regular (body text, descriptions)
     * - `'bodySmall'` — 1.3rem PlusJakartaSans SemiBold (card titles, list item names)
     * - `'labelMedium'` — 1.2rem PlusJakartaSans SemiBold, UPPERCASE with letter-spacing (metadata)
     * - `'labelSmall'` — 1.1rem PlusJakartaSans Medium (chip labels, badges).
     *
     * @default 'body'
     */
    $size?: PSize;
}

/**
 * A styled paragraph component for body text and labels.
 * Use the `$size` prop to select among body, labelMedium, or labelSmall typography.
 * The labelMedium variant automatically applies uppercase and letter-spacing.
 *
 * @param props        The styled component props including transient style props.
 * @param props.theme  The Emotion theme providing color and font tokens.
 * @param props.$align The optional text alignment override.
 * @returns A styled paragraph element with the specified typography and alignment.
 * @example
 * ```tsx
 * <P>Body text content</P>
 * <P $size="labelMedium">Metadata Label</P>
 * <P $size="labelSmall">Badge Text</P>
 * ```
 */
export const P = styled.p<ParagraphProps>`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: ${getPFontSize};
    font-weight: ${getPFontWeight};
    hyphens: auto;
    letter-spacing: ${getPLetterSpacing};
    line-height: ${getPLineHeight};
    margin: 0;
    padding: 0;
    text-align: ${({$align}) => $align ?? 'left'};
    text-transform: ${getPTextTransform};
`;